import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { query, orderBy, doc, getDoc, collectionGroup, collection, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { FaFilter, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import UniversalEvidenceWindow from '../evidence/UniversalEvidenceWindow';
import { getEvidenceActions } from '../evidence/evidenceActionConfigs';
import { EvidenceResultsModal } from '../modals/results/wrappers/CleanResultsModals';
import { useAuth } from '../../hooks/useAuth';
import '../../styles/BulletinTabs.css';
import '../../styles/EvidenceList.css';

const PublicEvidenceTab = () => {
  // Authentication
  const { user } = useAuth();
  
  // Pagination state
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  
  // Filter and UI state
  const [filterType, setFilterType] = useState('all');
  const [scanStatus, setScanStatus] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [location, setLocation] = useState('everywhere');
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  
  // Data caches
  const [publicCasefileIds, setPublicCasefileIds] = useState([]);
  const [casefileNames, setCasefileNames] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [userNames, setUserNames] = useState({}); // Reserved for displaying user information
  const [evidenceAnalysisStatus, setEvidenceAnalysisStatus] = useState({});
  
  // Refs for scroll detection
  const observerRef = useRef(null);
  
  // Constants
  const ITEMS_PER_PAGE = 10;

  // Fetch public casefiles (one-time, cached)
  useEffect(() => {
    const fetchPublicCasefiles = async () => {
      try {
        const publicCasefilesQuery = query(
          collection(db, 'casefiles'),
          where('isPublic', '==', true),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(publicCasefilesQuery);
        const casefileIds = [];
        const casefileNamesMap = {};
        
        snapshot.forEach((doc) => {
          const casefileData = doc.data();
          casefileIds.push(doc.id);
          casefileNamesMap[doc.id] = casefileData.title || casefileData.name || 'Untitled Case';
        });
        setPublicCasefileIds(casefileIds);
        setCasefileNames(casefileNamesMap);
        
        // Load first page of evidence
        if (casefileIds.length > 0) {
          loadEvidencePage(casefileIds, null, true);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('❌ ERROR fetching public casefiles:', error);
        console.error('Error details:', error.message, error.code);
        setLoading(false);
      }
    };

    fetchPublicCasefiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // loadEvidencePage not included intentionally - only runs once on mount

  // Load evidence page function
  const loadEvidencePage = useCallback(async (casefileIds, lastDocument = null, isFirstPage = false) => {
    if (isFirstPage) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      // Use collectionGroup to query evidence subcollections
      // But without orderBy to avoid index requirements for now
      let evidenceQuery = query(
        collectionGroup(db, 'evidence'),
        limit(ITEMS_PER_PAGE * 3) // Get more to account for filtering
      );

      // Skip pagination cursor for now to avoid index issues
      // if (lastDocument) {
      //   evidenceQuery = query(
      //     collectionGroup(db, 'evidence'),
      //     startAfter(lastDocument),
      //     limit(ITEMS_PER_PAGE)
      //   );
      // }

      const snapshot = await getDocs(evidenceQuery);
      const evidenceList = [];
      const userIds = new Set();

      // Filter evidence to only include items from public casefiles
      snapshot.forEach((doc) => {
        const evidenceData = { id: doc.id, ...doc.data() };
        
        if (evidenceData.casefileId && casefileIds.includes(evidenceData.casefileId)) {
          evidenceList.push(evidenceData);
          if (evidenceData.createdBy) {
            userIds.add(evidenceData.createdBy);
          }
        }
      });

      // Fetch user names for new evidence
      const userNamesMap = {};
      for (const userId of userIds) {
        try {
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            userNamesMap[userId] = userData.displayName || userData.email || 'Anonymous User';
          }
        } catch (error) {
          console.error('Error fetching user name:', error);
          userNamesMap[userId] = 'Anonymous User';
        }
      }
      setUserNames(prev => ({ ...prev, ...userNamesMap }));

      // Sort evidence client-side since we can't use Firestore orderBy
      evidenceList.sort((a, b) => {
        const aDate = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const bDate = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return bDate - aDate; // Newest first
      });

      // Update evidence list
      if (isFirstPage) {
        setEvidence(evidenceList);
      } else {
        setEvidence(prev => [...prev, ...evidenceList]);
      }

      // Disable pagination for now - just show all available evidence
      setLastDoc(null);
      setHasMore(false);

    } catch (error) {
      console.error('❌ ERROR loading evidence:', error);
      console.error('Error details:', error.message, error.code);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Load more evidence when sort changes
  useEffect(() => {
    if (publicCasefileIds.length > 0) {
      setEvidence([]);
      setLastDoc(null);
      setHasMore(true);
      loadEvidencePage(publicCasefileIds, null, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, publicCasefileIds]); // loadEvidencePage not included - stable function

  // Load analysis status for current evidence (optimized)
  useEffect(() => {
    if (evidence.length === 0) return;

    const loadAnalysisStatus = async () => {
      try {
        // Only query jobs for evidence we currently have loaded
        const evidenceIds = evidence.map(item => item.id);
        
        const jobsQuery = query(
          collectionGroup(db, 'jobs'),
          orderBy('createdAt', 'desc'),
          limit(100) // Limit to recent jobs for performance
        );
        
        const snapshot = await getDocs(jobsQuery);
        const evidenceWithJobs = new Set();
        
        snapshot.forEach((jobDoc) => {
          const jobData = jobDoc.data();
          if (jobData.evidenceId && jobData.status === 'completed' && evidenceIds.includes(jobData.evidenceId)) {
            evidenceWithJobs.add(jobData.evidenceId);
          }
        });
        
        // Update analysis status for current evidence
        const analysisStatusMap = { ...evidenceAnalysisStatus };
        evidence.forEach(evidenceItem => {
          analysisStatusMap[evidenceItem.id] = evidenceWithJobs.has(evidenceItem.id);
        });
        
        setEvidenceAnalysisStatus(analysisStatusMap);
      } catch (error) {
        console.error('Error loading analysis status:', error);
      }
    };

    loadAnalysisStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evidence]); // evidenceAnalysisStatus intentionally not included to avoid infinite loop

  // Infinite scroll setup
  useEffect(() => {
    const loadMore = () => {
      if (hasMore && !loadingMore && publicCasefileIds.length > 0) {
        loadEvidencePage(publicCasefileIds, lastDoc, false);
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    const currentObserverRef = observerRef.current;
    return () => {
      if (currentObserverRef) {
        observer.unobserve(currentObserverRef);
      }
    };
  }, [hasMore, loadingMore, publicCasefileIds, lastDoc, loadEvidencePage]);

  // Get evidence type for filtering
  const getEvidenceType = (evidence) => {
    const contentType = evidence.contentType || evidence.type || '';
    const fileName = evidence.fileName || '';
    
    if (contentType.startsWith('video/') || fileName.match(/\.(mp4|avi|mov|wmv|flv|webm)$/i)) {
      return 'video';
    } else if (contentType.startsWith('image/') || fileName.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i)) {
      return 'image';
    } else if (contentType.startsWith('audio/') || fileName.match(/\.(mp3|wav|ogg|aac|flac)$/i)) {
      return 'audio';
    } else {
      return 'document';
    }
  };

  // Filter and sort evidence
  const filteredAndSortedEvidence = useMemo(() => {
    let filtered = evidence;

    // Apply type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(item => getEvidenceType(item) === filterType);
    }

    // Apply scan status filter
    if (scanStatus !== 'all') {
      filtered = filtered.filter(item => {
        const hasAnalysis = evidenceAnalysisStatus[item.id] || false;
        return scanStatus === 'scanned' ? hasAnalysis : !hasAnalysis;
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return (b.createdAt?.toDate?.() || new Date(b.createdAt || 0)) - (a.createdAt?.toDate?.() || new Date(a.createdAt || 0));
        case 'oldest':
          return (a.createdAt?.toDate?.() || new Date(a.createdAt || 0)) - (b.createdAt?.toDate?.() || new Date(b.createdAt || 0));
        case 'name':
          return (a.fileName || '').localeCompare(b.fileName || '');
        case 'size':
          return (b.fileSize || 0) - (a.fileSize || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [evidence, filterType, scanStatus, sortBy, evidenceAnalysisStatus]);

  // Handle evidence click - open Results Modal
  const handleEvidenceClick = (evidenceItem) => {
    setSelectedEvidence(evidenceItem);
    setIsResultsModalOpen(true);
  };

  // Format file size helper
  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Get type counts and scan status counts for filter badges
  const filterCounts = useMemo(() => {
    const typeCounts = { all: evidence.length, video: 0, image: 0, audio: 0, document: 0 };
    const scanCounts = { all: evidence.length, scanned: 0, unscanned: 0 };
    
    evidence.forEach(item => {
      const type = getEvidenceType(item);
      typeCounts[type]++;
      
      const hasAnalysis = evidenceAnalysisStatus[item.id] || false;
      if (hasAnalysis) {
        scanCounts.scanned++;
      } else {
        scanCounts.unscanned++;
      }
    });
    
    return { typeCounts, scanCounts };
  }, [evidence, evidenceAnalysisStatus]);

  if (loading) {
    return (
      <div className="tab-content">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading public evidence...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-content">

      {/* Filter Controls */}
      <div className="evidence-controls">
        {/* Mobile: Collapsible Filter Button */}
        <button 
          className="mobile-filter-toggle"
          onClick={() => setFiltersExpanded(!filtersExpanded)}
          aria-label="Toggle filters"
        >
          <FaFilter />
          <span>Filters</span>
          {(filterType !== 'all' || scanStatus !== 'all' || location !== 'everywhere' || sortBy !== 'newest') && (
            <span className="filter-badge">
              {[
                filterType !== 'all',
                scanStatus !== 'all',
                location !== 'everywhere',
                sortBy !== 'newest'
              ].filter(Boolean).length}
            </span>
          )}
          {filtersExpanded ? <FaChevronUp /> : <FaChevronDown />}
        </button>

        {/* Desktop & Expanded Mobile: Filter Controls */}
        <div className={`filter-controls ${filtersExpanded ? 'expanded' : ''}`}>
          <div className="filter-group">
            <select 
              value={filterType} 
              onChange={(e) => setFilterType(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Types ({filterCounts.typeCounts.all})</option>
              <option value="video">Videos ({filterCounts.typeCounts.video})</option>
              <option value="image">Images ({filterCounts.typeCounts.image})</option>
              <option value="audio">Audio ({filterCounts.typeCounts.audio})</option>
              <option value="document">Documents ({filterCounts.typeCounts.document})</option>
            </select>
          </div>

          {/* Location (placeholder) */}
          <div className="filter-group">
            <select
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="filter-select"
            >
              <option value="everywhere">Everywhere</option>
              <option value="united-states">United States</option>
              <option value="canada">Canada</option>
              <option value="europe">Europe</option>
              <option value="australia">Australia</option>
            </select>
          </div>

          <div className="filter-group">
            <select 
              value={scanStatus} 
              onChange={(e) => setScanStatus(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Evidence ({filterCounts.scanCounts.all})</option>
              <option value="scanned">Analyzed ({filterCounts.scanCounts.scanned})</option>
              <option value="unscanned">Not Analyzed ({filterCounts.scanCounts.unscanned})</option>
            </select>
          </div>

          <div className="sort-group">
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="name">Name (A-Z)</option>
              <option value="size">Size (Largest)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Evidence Display using Standard EvidenceTile Layout */}
      <div className="evidence-list-container">
        {filteredAndSortedEvidence.length === 0 ? (
          <div className="empty-state">
            <FaFilter className="empty-icon" />
            <h3>No Evidence Found</h3>
            <p>
              {filterType !== 'all' || scanStatus !== 'all'
                ? 'Try adjusting your filters to see more evidence'
                : 'No public evidence available yet'
              }
            </p>
          </div>
        ) : (
          <>
            <ul className="evidence-list">
            {filteredAndSortedEvidence.map((item) => {
              // Generate action buttons for public context with full functionality
              const actionButtons = getEvidenceActions('public', {
                onOpenResults: () => {
                  // Open results modal for public evidence
                  setSelectedEvidence(item);
                  setIsResultsModalOpen(true);
                },
                onShare: () => {
                  // Share functionality - copy link to clipboard
                  if (navigator.share) {
                    navigator.share({
                      title: item.fileName,
                      text: `Check out this evidence: ${item.fileName}`,
                      url: window.location.href
                    }).catch(err => console.log('Error sharing:', err));
                  } else {
                    // Fallback: copy to clipboard
                    navigator.clipboard.writeText(window.location.href)
                      .then(() => alert('Link copied to clipboard!'))
                      .catch(err => console.error('Failed to copy:', err));
                  }
                }
              });
              
              return (
                <li key={item.id}>
                  <UniversalEvidenceWindow
                    evidence={item}
                    mode="tile"
                    actionButtons={actionButtons}
                    onOpenModal={handleEvidenceClick}
                    currentUser={user} // Pass authenticated user for proper ownership checks
                    formatFileSize={formatFileSize}
                    isSelected={false} // No selection in public view
                    onSelectionChange={null} // No selection in public view
                    casefileName={casefileNames[item.casefileId] || 'Unknown Case'}
                    casefileId={item.casefileId}
                    // Summary props (not used in tile mode but required)
                    evidenceSummary={item.summary || ''}
                    onSummaryChange={null}
                    isEditingSummary={false}
                    onEditingSummaryChange={null}
                    isSavingSummary={false}
                    onSavingSummaryChange={null}
                  />
                </li>
              );
            })}
            </ul>

            {/* Infinite scroll trigger */}
            {hasMore && (
              <div ref={observerRef} className="scroll-trigger">
                {loadingMore && (
                  <div className="loading-more">
                    <div className="spinner"></div>
                    <p>Loading more evidence...</p>
                  </div>
                )}
              </div>
            )}

            {/* End of results indicator */}
            {!hasMore && evidence.length > 0 && (
              <div className="end-of-results">
                <p>You've reached the end of public evidence</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Results Modal for Public Evidence */}
      {selectedEvidence && (
        <EvidenceResultsModal
          isOpen={isResultsModalOpen}
          onClose={() => {
            setIsResultsModalOpen(false);
            setSelectedEvidence(null);
          }}
          evidenceId={selectedEvidence.id}
          evidenceName={selectedEvidence.fileName}
          casefileId={selectedEvidence.casefileId}
          casefileName={casefileNames[selectedEvidence.casefileId] || 'Unknown Case'}
          currentUser={user} // Pass authenticated user for proper ownership checks
          slideMode={true}
        />
      )}

      {/* Evidence modal retired - Results Modal provides all functionality */}
    </div>
  );
};

export default PublicEvidenceTab;
